import React, {useEffect, useRef, useState} from 'react';
import {Pressable, StyleSheet, Text, View} from 'react-native';
import {faEye} from '@fortawesome/free-regular-svg-icons/faEye';
import {faEyeSlash} from '@fortawesome/free-regular-svg-icons/faEyeSlash';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {Formik, FormikHelpers} from 'formik';
import * as Yup from 'yup';

import Button from '../../components/ui-controls/Button';
import TextInput from '../../components/ui-controls/form/TextInput';
import ValidationMessage from '../../components/ui-controls/form/ValidationMessage';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Modal from '../../components/ui-controls/Modal';
import Checkbox from '../../components/ui-controls/web/CheckBox';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';

const ResetPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .required('Please enter a password')
      .min(8, 'Password must be at least 8 characters')
      .when('passwordValidationBypass', {
        is: false,
        then: (schema) => schema
          .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
          .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
          .matches(/[0-9]/, 'Password must contain at least one number')
          .matches(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/, 'Password must contain at least one symbol'),
    }),
  passwordValidationBypass: Yup.boolean(),
  token: Yup.string(),
});

// https://bottlemart.com.au/password?token=dd73de2fca367f94f71cd4062593026bd62949fa509224457276e678f5343ef6

const PasswordReset = ({
  navigation,
  route,
}: RouteNavigatorScreenProps<AppRoute.ACCOUNT_PASSWORD_RESET>) => {
  const {userStore} = useStores();
  const [isPasswordUpdatedModalVisible, setIsPasswordUpdatedModalVisible] = useState(false);
  const [isWeakPasswordCheckboxVisible, setWeakPasswordCheckboxVisible] = useState(false);

  const [isPasswordVisible, setPasswordVisible] = useState(false);

  const togglePasswordVisible = () => setPasswordVisible(!isPasswordVisible);

  const password = useRef(null);

  useEffect(() => {
    setWeakPasswordCheckboxVisible(userStore.showWeakPasswordCheckbox);
  }, [userStore.showWeakPasswordCheckbox]);

  const onSubmit = async (values: {password: string, passwordValidationBypass: boolean}, {setSubmitting, setFieldError}: FormikHelpers<{password: string, passwordValidationBypass: boolean}>) => {
    setSubmitting(true);
    try {
      
      // token is passed in via route params
      const result = await userStore.resetPassword(values, route.params.token);
      if (result.success) {
        setIsPasswordUpdatedModalVisible(true);
      } else {
        setFieldError('password', result.message);
      }
    } catch (err) {
      setFieldError('password', 'Failed to update password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const onConfirmationModalContinue = () => {
    setIsPasswordUpdatedModalVisible(false);
    navigation.navigate(AppRoute.HOME);
  } 

  //https://bottlemart.com.au/password?token=ff18ef3a3c91ce62bcc18e6443fa077eb7c6607c755cb00964870877616741e2
  return (
    <>
      <Formik
        initialValues={{password: '', passwordValidationBypass: false}}
        onSubmit={onSubmit}
        validationSchema={ResetPasswordSchema}
        validateOnChange={false}
        validateOnBlur={true}
        validateOnMount={false}
        >
        {({handleChange, handleBlur, handleSubmit, errors, touched, values, setFieldValue}) => {
          return (<ContentView style={styles.main}>
            <View>
              <TextInput
                ref={password}
                icon="key"
                placeholder="New password"
                secureTextEntry={!isPasswordVisible}
                autoCompleteType="password"
                autoCapitalize="none"
                returnKeyType="go"
                returnKeyLabel="go"
                onChangeText={handleChange('password')}
                onBlur={(e) => {
                  handleBlur('password')(e);
                }}
                error={errors.password}
                touched={touched.password}
                onSubmitEditing={() => handleSubmit()}
              />
              <Pressable
                onPress={() => togglePasswordVisible()}
                style={styles.searchBarPost}>
                <FontAwesomeIcon
                  icon={isPasswordVisible ? faEye : faEyeSlash}
                  size={20}
                  style={{color: 'lightgrey'}}
                />
              </Pressable>
              {errors.password && touched.password ? (
                <ValidationMessage style={styles.validationError}>
                  {errors.password}
                </ValidationMessage>
              ) : null}
            </View>
            {values.passwordValidationBypass || isWeakPasswordCheckboxVisible || (errors.password && touched.password) ? (
              <View
                style={[
                  styles.checkboxContainer,
                ]}>
                <Checkbox
                  boxType="square"
                  checked={values.passwordValidationBypass}
                  onChange={() =>
                    setFieldValue(
                      'passwordValidationBypass',
                      !values.passwordValidationBypass,
                    )
                  }
                  style={styles.checkbox}
                />
                <Text style={styles.compromisedPasswordText}>
                  This password doesn&apos;t meet minimum strength requirements
                  and/or has been found in a list of leaked passwords. By
                  ticking this box you acknowledge that you wish to
                  continue with this password.
                </Text>
              </View>
            ) : null}
            <Button
              title="Change password"
              onPress={handleSubmit}
              containerStyle={styles.button}
            />
          </ContentView>
        )}}
      </Formik>
      <Modal isVisible={isPasswordUpdatedModalVisible} title="Password updated. Please log in with your new password.">
        <Button title="Continue" onPress={onConfirmationModalContinue} width={150} style={{marginTop: 16}} />
      </Modal>
    </>
  );
};

export default PasswordReset;

const styles = StyleSheet.create({
  main: {
    flex: 1,
    backgroundColor: 'white',
    paddingVertical: 24,
    paddingHorizontal: 16,
    alignItems: 'stretch',
    justifyContent: 'center',
  },
  button: {
    marginTop: 16,
  },
  screenText: {
    fontSize: 16,
    marginVertical: 16,
  },
  textDefault: {
    backgroundColor: 'white',
    color: 'grey',
  },
  emailInput: {
    fontSize: 20,
    fontWeight: '400',
    fontFamily: undefined,
    paddingLeft: 12,
    height: 32,
    borderColor: 'grey',
    borderWidth: 0.5,
    borderRadius: 4,
    color: theme.colour.text.form.textInput.primary,
  },
  searchBarPost: {
    position: 'absolute',
    right: 4,
    top: 8,
  },
  label: {
    fontSize: 20,
    fontWeight: '400',
    fontFamily: undefined,
    borderColor: 'grey',
    borderWidth: 0.5,
    borderRadius: 4,
    color: theme.colour.text.heading.primary,
    flex: 1,
  },
  twoCol: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
  },
  validationError: {
    fontSize: 16,
    paddingHorizontal: 4,
  },
  checkbox: {
    alignSelf: 'center',
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 8,
    paddingHorizontal: 4,
  },
  compromisedPasswordText: {
    alignSelf: 'center',
    color: 'red',
    flex: 1,
    fontSize: 12,
    justifyContent: 'center',
    marginBottom: 4,
    marginLeft: 8,
  },
});
